'use client';

import React, { useState } from 'react';
import { Search, Hash, Layers, User } from 'lucide-react';
import { toast } from 'sonner';
import Icon from '@/components/ui/AppIcon';

type SearchType = 'block' | 'tx' | 'address';

interface SearchTypeConfig {
  value: SearchType;
  label: string;
  IconComponent: React.ElementType;
  placeholder: string;
}

const searchTypes: SearchTypeConfig[] = [
  {
    value: 'block',
    label: 'Block',
    IconComponent: Layers,
    placeholder: 'Enter block height or hash (e.g. 4827341)',
  },
  {
    value: 'tx',
    label: 'Transaction',
    IconComponent: Hash,
    placeholder: 'Enter transaction hash (0x...)',
  },
  {
    value: 'address',
    label: 'Address',
    IconComponent: User,
    placeholder: 'Enter wallet address (rtwo1...)',
  },
];

export default function ExplorerSearch() {
  const [query, setQuery] = useState('');
  const [type, setType] = useState<SearchType>('block');

  const current = searchTypes.find((t) => t.value === type)!;

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (!query.trim()) return;
    toast.info(`Searching for ${type}: ${query.trim()}`);
  };

  return (
    <div data-component-id="src/app/components/ExplorerSearch.tsx:51:4" data-component-path="src/app/components/ExplorerSearch.tsx" data-component-line="51" data-component-end-line="97" data-component-file="ExplorerSearch.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22bg-card%20border%20border-border%20rounded-xl%20p-4%22%7D" className="bg-card border border-border rounded-xl p-4">
      <form data-component-id="src/app/components/ExplorerSearch.tsx:52:6" data-component-path="src/app/components/ExplorerSearch.tsx" data-component-line="52" data-component-end-line="96" data-component-file="ExplorerSearch.tsx" data-component-name="form" data-component-content="%7B%22elementName%22%3A%22form%22%2C%22className%22%3A%22flex%20flex-col%20sm%3Aflex-row%20gap-3%22%7D" onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-3">
        {/* Type selector */}
        <div data-component-id="src/app/components/ExplorerSearch.tsx:54:8" data-component-path="src/app/components/ExplorerSearch.tsx" data-component-line="54" data-component-end-line="73" data-component-file="ExplorerSearch.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20rounded-lg%20border%20border-border%20overflow-hidden%20shrink-0%22%7D" className="flex rounded-lg border border-border overflow-hidden shrink-0">
          {searchTypes.map((t) => {
            const Icon = t.IconComponent;
            return (
              <button data-component-id="src/app/components/ExplorerSearch.tsx:58:14" data-component-path="src/app/components/ExplorerSearch.tsx" data-component-line="58" data-component-end-line="70" data-component-file="ExplorerSearch.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22type%22%3A%22button%22%7D"
                key={`search-type-${t.value}`}
                type="button"
                onClick={() => setType(t.value)}
                className={`flex items-center gap-1.5 px-3 py-2 text-xs font-medium transition-all duration-150 ${
                  type === t.value
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                }`}
              >
                <Icon data-component-id="src/app/components/ExplorerSearch.tsx:68:16" data-component-path="src/app/components/ExplorerSearch.tsx" data-component-line="68" data-component-end-line="68" data-component-file="ExplorerSearch.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%7D" size={14} />
                {t.label}
              </button>
            );
          })}
        </div>

        {/* Input */}
        <div data-component-id="src/app/components/ExplorerSearch.tsx:76:8" data-component-path="src/app/components/ExplorerSearch.tsx" data-component-line="76" data-component-end-line="88" data-component-file="ExplorerSearch.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22relative%20flex-1%22%7D" className="relative flex-1">
          <Search data-component-id="src/app/components/ExplorerSearch.tsx:77:10" data-component-path="src/app/components/ExplorerSearch.tsx" data-component-line="77" data-component-end-line="80" data-component-file="ExplorerSearch.tsx" data-component-name="Search" data-component-content="%7B%22elementName%22%3A%22Search%22%2C%22className%22%3A%22absolute%20left-3%20top-1%2F2%20-translate-y-1%2F2%20text-muted-foreground%22%7D"
            size={14}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
          />
          <input data-component-id="src/app/components/ExplorerSearch.tsx:81:10" data-component-path="src/app/components/ExplorerSearch.tsx" data-component-line="81" data-component-end-line="87" data-component-file="ExplorerSearch.tsx" data-component-name="input" data-component-content="%7B%22elementName%22%3A%22input%22%2C%22type%22%3A%22text%22%2C%22value%22%3A%22%5Bvar%3Aquery%5D%22%2C%22className%22%3A%22w-full%20bg-muted%20border%20border-border%20rounded-lg%20pl-9%20pr-4%20py-2%20text-sm%20text-foreground%20placeholder%3Atext-muted-foreground%20focus%3Aoutline-none%20focus%3Aring-1%20focus%3Aring-ring%20focus%3Aborder-primary%20transition-all%20duration-150%22%7D"
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={current.placeholder}
            className="w-full bg-muted border border-border rounded-lg pl-9 pr-4 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring focus:border-primary transition-all duration-150"
          />
        </div>

        <button data-component-id="src/app/components/ExplorerSearch.tsx:90:8" data-component-path="src/app/components/ExplorerSearch.tsx" data-component-line="90" data-component-end-line="95" data-component-file="ExplorerSearch.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22type%22%3A%22submit%22%2C%22className%22%3A%22px-5%20py-2%20bg-primary%20text-primary-foreground%20rounded-lg%20text-sm%20font-semibold%20hover%3Aopacity-90%20active%3Ascale-95%20transition-all%20duration-150%20shrink-0%22%2C%22textContent%22%3A%22Search%22%7D"
          type="submit"
          className="px-5 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-semibold hover:opacity-90 active:scale-95 transition-all duration-150 shrink-0"
        >
          Search
        </button>
      </form>
    </div>
  );
}
